/**
 * GlobalSearch – Volltext-Suche im Header
 * Sucht ueber: Klienten, Aufgaben, Dokumente, Personen, Korrespondenz, Massnahmen, Berichte, Wiki
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, User, FileText, CheckSquare, Users, Loader2, Mail, Shield, BookOpen, Scale } from 'lucide-react';
import { useTabs } from '@/contexts/TabContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const TYPE_CONFIG = {
  klienten: { icon: User, label: 'Betreuungsakten', color: 'text-blue-600 bg-blue-50' },
  aufgaben: { icon: CheckSquare, label: 'Aufgaben', color: 'text-amber-600 bg-amber-50' },
  dokumente: { icon: FileText, label: 'Dokumente', color: 'text-green-600 bg-green-50' },
  personen: { icon: Users, label: 'Personen', color: 'text-teal-600 bg-teal-50' },
  korrespondenz: { icon: Mail, label: 'Korrespondenz', color: 'text-violet-600 bg-violet-50' },
  massnahmen: { icon: Shield, label: 'Massnahmen', color: 'text-rose-600 bg-rose-50' },
  berichte: { icon: Scale, label: 'Berichte', color: 'text-indigo-600 bg-indigo-50' },
  wiki: { icon: BookOpen, label: 'Betreuungs-Wiki', color: 'text-cyan-600 bg-cyan-50' },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const { openTab } = useTabs();
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(q)}&limit=8`);
      const data = await res.json();
      if (data.success) setResults(data.results);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => search(query), 300);
    } else {
      setResults(null);
    }
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSelect = (item) => {
    if (item.typ === 'klient') {
      openTab(`/akten/${item.id}`, { label: item.label, type: 'akte' });
    } else if (item.typ === 'aufgabe') {
      openTab('/aufgaben', { label: 'Aufgaben', type: 'aufgaben' });
    } else if (item.typ === 'person') {
      openTab(`/personen/${item.id}`, { label: item.label, type: 'person' });
    } else if (item.typ === 'korrespondenz' && item.klient_id) {
      openTab(`/akten/${item.klient_id}`, { label: item.detail, type: 'akte' });
    } else if (item.typ === 'massnahme' && item.klient_id) {
      openTab(`/akten/${item.klient_id}`, { label: item.label, type: 'akte' });
    } else if (item.typ === 'bericht' && item.klient_id) {
      openTab(`/akten/${item.klient_id}`, { label: item.label, type: 'akte' });
    } else if (item.typ === 'wiki') {
      openTab('/admin', { label: 'Wiki', type: 'admin' });
    } else {
      openTab('/posteingang', { label: 'Posteingang', type: 'posteingang' });
    }
    setOpen(false);
    setQuery('');
  };

  const totalResults = results ? Object.values(results).reduce((s, arr) => s + arr.length, 0) : 0;

  return (
    <div ref={containerRef} className="relative" data-testid="global-search">
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
        open ? 'w-80 border-violet-300 bg-white shadow-md' : 'w-56 border-slate-200 bg-slate-50 hover:bg-white'
      }`}>
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Suche... (Ctrl+K)"
          className="flex-1 bg-transparent text-sm outline-none text-slate-700 placeholder:text-slate-400"
          data-testid="global-search-input"
        />
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500" />}
        {query && (
          <button onClick={() => { setQuery(''); setResults(null); }} className="text-slate-400 hover:text-slate-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && results && totalResults > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl z-50 max-h-[400px] overflow-y-auto"
          data-testid="search-results-dropdown">
          {Object.entries(results).map(([type, items]) => {
            if (!items || !items.length) return null;
            const config = TYPE_CONFIG[type];
            if (!config) return null;
            const Icon = config.icon;
            return (
              <div key={type}>
                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b">
                  {config.label} ({items.length})
                </div>
                {items.map(item => (
                  <button key={`${type}-${item.id}`} onClick={() => handleSelect(item)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-violet-50 text-left transition-colors border-b border-slate-50"
                    data-testid={`search-result-${item.id}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{item.label}</p>
                      {item.detail && <p className="text-xs text-slate-400 truncate">{item.detail}</p>}
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
          <div className="px-3 py-2 text-center border-t bg-slate-50">
            <span className="text-xs text-slate-400">{totalResults} Ergebnis{totalResults !== 1 ? 'se' : ''} gefunden</span>
          </div>
        </div>
      )}

      {open && query.length >= 2 && !loading && totalResults === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl z-50 p-6 text-center">
          <p className="text-sm text-slate-500">Keine Ergebnisse fuer "{query}"</p>
        </div>
      )}
    </div>
  );
}
